import { useState, type FormEvent } from 'react'
import { Alert, Box, Button, Chip, CircularProgress, TextField, Typography } from '@mui/material'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { DASHBOARD_AUTH_PATHS, DASHBOARD_HOME, normalizeOrgRole } from '@yourownmetric/shared'

import { useAcceptInviteMutation, useGetInvitePreviewQuery } from '@/apis/teamApi'
import { ROLE_HELP, ROLE_LABELS } from '@/apis/teamTypes'
import { useAppDispatch, useAppSelector } from '@/app/hooks'

import { logout, setCredentials } from './authSlice'

/** API error code from an RTK Query error, if any. */
function errorCode(err: unknown): string | undefined {
    const data = (err as { data?: { error?: { code?: string; message?: string } } } | undefined)
        ?.data
    return data?.error?.code
}
function errorMessage(err: unknown): string | undefined {
    return (err as { data?: { error?: { message?: string } } } | undefined)?.data?.error?.message
}

const STATUS_TEXT = {
    accepted: 'This invitation has already been used.',
    revoked: 'This invitation was withdrawn or replaced by a newer one. Ask for a new invite.',
    expired: 'This invitation has expired. Ask the person who invited you to send a new one.',
} as const

/** Target of the emailed team invite: `/accept-invite?token=…`. */
export function AcceptInvitePage() {
    const [params] = useSearchParams()
    const token = params.get('token') ?? ''
    const navigate = useNavigate()
    const dispatch = useAppDispatch()
    const session = useAppSelector(s => s.auth)
    const {
        data: invite,
        isLoading,
        error: previewError,
    } = useGetInvitePreviewQuery(token, { skip: !token })
    const [accept, { isLoading: accepting, error: acceptError }] = useAcceptInviteMutation()
    const [name, setName] = useState('')
    const [password, setPassword] = useState('')
    const [confirm, setConfirm] = useState('')
    const mismatch = confirm.length > 0 && password !== confirm

    const here = `${DASHBOARD_AUTH_PATHS.acceptInvite}?token=${encodeURIComponent(token)}`

    const join = async (body: { token: string; name?: string; password?: string }) => {
        try {
            const res = await accept(body).unwrap()
            dispatch(setCredentials({ token: res.token, user: res.user, tenant: res.tenant }))
            navigate(DASHBOARD_HOME)
        } catch {
            // shown below
        }
    }

    if (!token || previewError) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, textAlign: 'center' }}>
                <Typography variant="h4">Invalid invitation</Typography>
                <Typography color="text.secondary">
                    This link is incomplete or no longer exists. Ask for a new invite.
                </Typography>
                <Button component={Link} to={DASHBOARD_AUTH_PATHS.login} variant="text">
                    Go to sign in
                </Button>
            </Box>
        )
    }
    if (isLoading || !invite) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
            </Box>
        )
    }

    const role = normalizeOrgRole(invite.role)
    const header = (
        <Box
            sx={{
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                alignItems: 'center',
            }}
        >
            <Typography variant="h4">Join {invite.orgName}</Typography>
            <Typography color="text.secondary">
                {invite.inviterName ? `${invite.inviterName} invited` : 'You were invited'}{' '}
                <b>{invite.email}</b>
            </Typography>
            <Chip label={ROLE_LABELS[role]} color="primary" size="small" />
            <Typography variant="body2" color="text.secondary">
                {ROLE_HELP[role]}
            </Typography>
        </Box>
    )

    if (invite.status !== 'pending') {
        return (
            <Box
                sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 420, mx: 'auto' }}
            >
                {header}
                <Alert severity="warning">{STATUS_TEXT[invite.status]}</Alert>
                <Button component={Link} to={DASHBOARD_AUTH_PATHS.login}>
                    Go to sign in
                </Button>
            </Box>
        )
    }

    const failure =
        acceptError &&
        (errorCode(acceptError) === 'PLAN_LIMIT'
            ? 'This organization has no free seats on its current plan. Ask an admin to upgrade.'
            : (errorMessage(acceptError) ?? 'Could not accept the invitation.'))

    // Signed in already.
    if (session.token && session.user) {
        const sameEmail = session.user.email.toLowerCase() === invite.email.toLowerCase()
        return (
            <Box
                sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 420, mx: 'auto' }}
            >
                {header}
                {failure && <Alert severity="error">{failure}</Alert>}
                {sameEmail ? (
                    <Button
                        variant="contained"
                        size="large"
                        disabled={accepting}
                        onClick={() => join({ token })}
                    >
                        {accepting ? 'Joining…' : `Join ${invite.orgName}`}
                    </Button>
                ) : (
                    <>
                        <Alert severity="info">
                            You're signed in as {session.user.email}. This invitation is for{' '}
                            {invite.email}.
                        </Alert>
                        <Button
                            variant="contained"
                            onClick={() => {
                                dispatch(logout())
                                navigate(here, { replace: true })
                            }}
                        >
                            Sign out and continue as {invite.email}
                        </Button>
                    </>
                )}
            </Box>
        )
    }

    // Has an account: sign in first, then come back here.
    if (invite.accountExists) {
        return (
            <Box
                sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 420, mx: 'auto' }}
            >
                {header}
                <Alert severity="info">You already have a YOM account. Sign in to accept.</Alert>
                <Button
                    variant="contained"
                    size="large"
                    component={Link}
                    to={`${DASHBOARD_AUTH_PATHS.login}?next=${encodeURIComponent(here)}`}
                >
                    Sign in to accept
                </Button>
            </Box>
        )
    }

    // New here: create the account and join in one step.
    const submit = (e: FormEvent) => {
        e.preventDefault()
        if (!mismatch) {
            void join({ token, name, password })
        }
    }
    return (
        <Box
            component="form"
            onSubmit={submit}
            sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 420, mx: 'auto' }}
        >
            {header}
            {failure && <Alert severity="error">{failure}</Alert>}
            <TextField label="Email" value={invite.email} disabled fullWidth />
            <TextField
                label="Your name"
                value={name}
                onChange={e => setName(e.target.value)}
                required
                fullWidth
                autoComplete="name"
                inputProps={{ maxLength: 100 }}
            />
            <TextField
                label="Password"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                fullWidth
                autoComplete="new-password"
                inputProps={{ minLength: 8, maxLength: 128 }}
                helperText="At least 8 characters"
            />
            <TextField
                label="Confirm password"
                type="password"
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                required
                fullWidth
                autoComplete="new-password"
                error={mismatch}
                helperText={mismatch ? 'Passwords do not match' : ' '}
            />
            <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={accepting || mismatch}
                fullWidth
            >
                {accepting ? 'Creating your account…' : 'Create account and join'}
            </Button>
        </Box>
    )
}
